import { GroupSecurity } from '@quromedical/fhir-common';
import type {
  OrganizationGetAllResponse,
  OrganizationSearch,
} from '@quromedical/models';
import { CrudForm } from 'components/form';
import { WithGroup } from 'core/auth';
import {
  columnsLarge,
  columnsSmall,
  initialSearch,
  OrganizationCreateCard,
  type OrganizationTable,
  searchFields,
} from 'core/organization';
import { PaginatedTable } from 'core/table';
import { Section, ToggleCard } from 'design-system';
import { router } from 'expo-router';
import type { FetcherOptions } from 'hooks/usePaginatedData';
import { useResponsive } from 'hooks/useResponsive';
import { OrganizationApi } from 'integration/aggregate';
import type { OrganizationStackScreenProps } from 'navigation';
import type React from 'react';
import { useCallback, useState } from 'react';
import { ScrollView } from 'react-native';
import { strings } from 'strings';

const api = new OrganizationApi();

const getDataFromResponse = (
  res: OrganizationGetAllResponse,
): OrganizationTable[] =>
  res.organizations.map((p) => ({
    id: p.id,
    name: p.general.name,
    type: p.general.type,
    telecomEmail: p.contact.telecomEmail,
    telecomPhoneNumbers: p.contact.telecomPhoneNumbers?.[0],
    addressLines: p.contact.addressLines?.[1],
    addressCity: p.contact.addressCity,
    addressPostalCode: p.contact.addressPostalCode,
    contact: p.contact,
    general: p.general,
  }));

export const OrganizationListScreen: React.FC<
  OrganizationStackScreenProps<'List'>
> = () => {
  const showHeader = useResponsive({ md: true }, false);
  const isStriped = useResponsive({ md: true }, false);
  const columns = useResponsive({ md: columnsLarge }, columnsSmall);

  const [search, setSearch] = useState<OrganizationSearch>(initialSearch);

  const fetcher = useCallback(
    (options?: FetcherOptions) =>
      api.getAll({
        address: search.address ? search.address : undefined,
        name: search.name ? search.name : undefined,
        type: search.type,
        pageToken: options?.nextToken,
        ...options,
      }),
    [search],
  );

  const onRowPress = useCallback(
    (row: OrganizationTable) =>
      router.push({
        pathname: `/admin/organization/${row.id}`,
      }),
    [],
  );

  return (
    <ScrollView>
      <WithGroup groups={[GroupSecurity.OrganizationCreate]}>
        <Section unsetZIndex>
          <ToggleCard
            icon="medical-services"
            title={strings.OrganizationToggleCardTitle}
            textExpand={strings.OrganizationToggleCardExpandButtonText}
            textCollapse={strings.OrganizationToggleCardCollapseButtonText}
            contentWrapperProps={{ paddingHorizontal: 0, paddingVertical: 0 }}
          >
            <OrganizationCreateCard />
          </ToggleCard>
        </Section>
      </WithGroup>
      <Section unsetZIndex>
        <CrudForm
          fields={searchFields}
          initialValues={initialSearch}
          showSubmitButton={false}
          onChange={setSearch}
        />
      </Section>

      <Section>
        <PaginatedTable
          cacheKey={`Organization${JSON.stringify(search)}`}
          fetcher={fetcher}
          columns={columns}
          getDataFromResponse={getDataFromResponse}
          showHeader={showHeader}
          isStriped={isStriped}
          onRowPress={onRowPress}
        />
      </Section>
    </ScrollView>
  );
};
